import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sparkles,
  ImageIcon,
  Store,
  LayoutGrid,
  Bell,
  Heart,
  UserCheck,
  Check,
  Users,
  type LucideIcon,
} from 'lucide-react';

import { Badge } from '@/components/atoms/badge';
import { cn, formatPrice } from '@/lib/utils';
import type { AdType, AdTypeConfig } from '../../../types/ads.types';
import { getPriceDisplayForType, getTypeConfig } from '../../../utils/pricing.utils';
import type { Currency } from '@/lib/utils/constants';
import type { ObjectiveFilter } from '../../../stores/campaign-wizard.store';

// ============ Types ============

interface CampaignTypeOption {
  value: AdType;
  label: string;
  icon: LucideIcon;
  description: string;
  category: 'visibility' | 'notification';
}

interface AudienceData {
  subscribersCount: number;
  likersCount: number;
  isLoading?: boolean;
}

interface StepTypeSelectionProps {
  selectedType: AdType | null;
  onSelectType: (type: AdType) => void;
  currency: Currency;
  typeConfigs?: AdTypeConfig[];
  filterObjective?: ObjectiveFilter;
  audienceData?: AudienceData;
}

// ============ Campaign Types Config ============

const CAMPAIGN_TYPE_OPTIONS: CampaignTypeOption[] = [
  // Visibility campaigns
  {
    value: 'sponsored_product',
    label: 'Produit sponsorisé',
    icon: Sparkles,
    description: 'Votre produit apparaît en premier dans les résultats de recherche',
    category: 'visibility',
  },
  {
    value: 'homepage_carousel',
    label: 'Carousel accueil',
    icon: ImageIcon,
    description: "Grande visibilité sur la page d'accueil de l'application",
    category: 'visibility',
  },
  {
    value: 'featured_shop',
    label: 'Boutique en vedette',
    icon: Store,
    description: 'Mettez votre boutique en avant auprès des visiteurs',
    category: 'visibility',
  },
  {
    value: 'category_banner',
    label: 'Bannière catégorie',
    icon: LayoutGrid,
    description: 'Bannière publicitaire dans une catégorie de produits',
    category: 'visibility',
  },
  // Notification campaigns
  {
    value: 'push_shop_subscribers',
    label: 'Notifier mes abonnés',
    icon: UserCheck,
    description: 'Envoyez une notification à tous vos abonnés',
    category: 'notification',
  },
  {
    value: 'push_product_likers',
    label: 'Notifier les fans produits',
    icon: Heart,
    description: 'Notifiez les personnes qui ont aimé vos produits',
    category: 'notification',
  },
  {
    value: 'push_single_product_likers',
    label: "Notifier fans d'un produit",
    icon: Bell,
    description: "Notifiez les personnes qui ont aimé un produit spécifique",
    category: 'notification',
  },
];

// ============ Component ============

export function StepTypeSelection({
  selectedType,
  onSelectType,
  currency,
  typeConfigs,
  filterObjective,
  audienceData,
}: StepTypeSelectionProps) {
  const { t } = useTranslation();

  // Filter out disabled types from server
  const enabledTypes = React.useMemo(() => {
    if (!typeConfigs) return CAMPAIGN_TYPE_OPTIONS;
    const enabledTypeValues = new Set(
      typeConfigs.filter((c) => c.enabled).map((c) => c.type)
    );
    return CAMPAIGN_TYPE_OPTIONS.filter((t) =>
      enabledTypeValues.has(t.value)
    );
  }, [typeConfigs]);

  // Apply objective filter if present
  const filteredTypes = React.useMemo(() => {
    if (!filterObjective) return enabledTypes;

    // Map objective to categories
    if (filterObjective === 'visibility') {
      // For visibility, only show carousel and banner types
      return enabledTypes.filter((t) =>
        t.value === 'homepage_carousel' || t.value === 'category_banner'
      );
    }
    if (filterObjective === 'notification') {
      return enabledTypes.filter((t) => t.category === 'notification');
    }
    return enabledTypes;
  }, [enabledTypes, filterObjective]);

  const visibilityTypes = filteredTypes.filter((t) => t.category === 'visibility');
  const notificationTypes = filteredTypes.filter((t) => t.category === 'notification');

  // Check if we're in filtered mode
  const isFiltered = filterObjective !== null && filterObjective !== undefined;

  // Get price display from server config
  const getPriceDisplay = (type: CampaignTypeOption): string => {
    const config = getTypeConfig(type.value, typeConfigs);
    return getPriceDisplayForType(type.value, config, currency);
  };

  // Get audience count for notification types
  const getAudienceForType = (type: AdType): { count: number; label: string; icon: string } | null => {
    if (!audienceData) return null;

    switch (type) {
      case 'push_shop_subscribers':
        return { count: audienceData.subscribersCount, label: 'abonnés', icon: '👥' };
      case 'push_product_likers':
      case 'push_single_product_likers':
        return { count: audienceData.likersCount, label: 'fans', icon: '❤️' };
      default:
        return null;
    }
  };

  // Calculate estimated cost for notification types
  const getEstimatedCost = (type: AdType, audienceCount: number): number | null => {
    const config = getTypeConfig(type, typeConfigs);
    if (!config || config.priceUnit !== 'per_1000') return null;

    // Get price in user's currency
    const price = currency === 'EUR' ? config.priceEur
      : currency === 'USD' ? config.priceUsd
      : config.priceKmf;

    return (audienceCount / 1000) * price;
  };

  // Get title/subtitle based on filter
  const getTitle = () => {
    if (filterObjective === 'visibility') {
      return t('ads.wizard.step1.visibility_title', 'Où souhaitez-vous apparaître ?');
    }
    if (filterObjective === 'notification') {
      return t('ads.wizard.step1.notification_title', 'Qui souhaitez-vous notifier ?');
    }
    return t('ads.wizard.step1.title', 'Que souhaitez-vous promouvoir ?');
  };

  const getSubtitle = () => {
    if (filterObjective === 'visibility') {
      return t('ads.wizard.step1.visibility_subtitle', 'Choisissez où afficher votre bannière publicitaire');
    }
    if (filterObjective === 'notification') {
      return t('ads.wizard.step1.notification_subtitle', 'Sélectionnez votre audience cible');
    }
    return t('ads.wizard.step1.subtitle', 'Choisissez le type de campagne adapté à votre objectif');
  };

  // Render type card - compact on mobile, expanded on desktop
  const renderTypeCard = (type: CampaignTypeOption) => {
    const Icon = type.icon;
    const isSelected = selectedType === type.value;
    const isNotification = type.category === 'notification';
    const audience = isNotification ? getAudienceForType(type.value) : null;
    const estimatedCost = audience ? getEstimatedCost(type.value, audience.count) : null;

    return (
      <button
        key={type.value}
        type="button"
        onClick={() => onSelectType(type.value)}
        className={cn(
          'relative flex items-start gap-3 p-3 sm:p-4 rounded-xl border-2 transition-all text-left',
          'sm:flex-col sm:items-start sm:gap-2',
          isSelected
            ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
            : 'border-border hover:border-primary/50 hover:bg-muted/50 active:bg-muted/70'
        )}
      >
        {isSelected && (
          <div className="absolute top-3 right-3">
            <Check className="size-5 text-primary" />
          </div>
        )}
        <div
          className={cn(
            'size-10 shrink-0 rounded-lg flex items-center justify-center',
            isSelected
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted'
          )}
        >
          <Icon className="size-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium">{type.label}</p>
          <p className="text-sm text-muted-foreground line-clamp-2 sm:line-clamp-none">
            {type.description}
          </p>

          {/* Audience metrics for notification types */}
          {audience && !audienceData?.isLoading && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              <Badge variant="secondary" className="text-xs">
                <Users className="size-3 mr-1" />
                {audience.icon} {audience.count.toLocaleString()} {audience.label}
              </Badge>
              {estimatedCost !== null && estimatedCost > 0 && (
                <Badge variant="outline" className="text-xs">
                  ~{formatPrice(estimatedCost, currency)}
                </Badge>
              )}
            </div>
          )}

          {/* Price badge - mobile only for non-notification types */}
          {!isNotification && (
            <Badge variant="secondary" className="text-xs font-medium mt-2 sm:mt-0 sm:hidden">
              {getPriceDisplay(type)}
            </Badge>
          )}
        </div>

        {/* Price badge - desktop only for non-notification types, or price per 1000 for notifications */}
        <Badge variant="secondary" className="text-xs font-medium hidden sm:inline-flex sm:mt-2">
          {getPriceDisplay(type)}
        </Badge>
      </button>
    );
  };

  // Filtered view - simpler layout
  if (isFiltered) {
    const typesToShow = filterObjective === 'visibility' ? visibilityTypes : notificationTypes;
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">{getTitle()}</h2>
          <p className="text-muted-foreground">{getSubtitle()}</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {typesToShow.map(renderTypeCard)}
        </div>
      </div>
    );
  }

  // Full view - show all categories
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-semibold mb-2">{getTitle()}</h2>
        <p className="text-muted-foreground">{getSubtitle()}</p>
      </div>

      {/* Visibility campaigns */}
      {visibilityTypes.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Sparkles className="size-4" />
            {t('ads.wizard.step1.visibility', 'Augmenter la visibilité')}
          </h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {visibilityTypes.map(renderTypeCard)}
          </div>
        </div>
      )}

      {/* Notification campaigns */}
      {notificationTypes.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Bell className="size-4" />
            {t('ads.wizard.step1.notifications', 'Envoyer des notifications')}
          </h3>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {notificationTypes.map(renderTypeCard)}
          </div>
        </div>
      )}
    </div>
  );
}

export { CAMPAIGN_TYPE_OPTIONS };
